import { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, MapPin, Mail, Phone, MessageCircle, CheckCircle2, Sparkles,
} from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import { api } from "@/lib/api";
import { whatsappUrl, CONTACT_EMAIL } from "@/lib/channels";
import { PROJECT_TYPES, BUDGET_RANGES, COUNTRIES } from "@/data/leadOptions";

export default function Contact() {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const payload = { ...data, source: "contact_page", website: data.website || "" };
      await api.post("/contact", payload);
      setSuccess(payload);
      toast.success("Thank you — we'll be in touch shortly.");
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Something went wrong. Please try again or message us on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHeader
        overline="Contact"
        title="Let's talk about what you're building"
        subtitle="Share a few details about your project and a senior member of our team will get back to you within one business day."
        testid="contact-page-header"
      />

      <section className="py-12 md:py-16">
        <div className="container-zx grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <div className="card-surface p-6">
              <div className="overline mb-2">Mumbai, India</div>
              <div className="text-white font-semibold">Corporate &amp; Delivery Center</div>
              <div className="mt-3 space-y-2 text-sm text-ink-200">
                <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-cyan" /> Mumbai, Maharashtra, India</div>
                <div className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-cyan" /> <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white" data-testid="contact-mumbai-email">{CONTACT_EMAIL}</a></div>
                <div className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-cyan" /> <a href="tel:+918828271040" className="hover:text-white" data-testid="contact-mumbai-phone">+91 88282 71040</a></div>
              </div>
            </div>
            <div className="card-surface p-6">
              <div className="overline mb-2">Dubai, UAE</div>
              <div className="text-white font-semibold">International Business Development</div>
              <div className="mt-3 space-y-2 text-sm text-ink-200">
                <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-cyan" /> Dubai, United Arab Emirates</div>
                <div className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-cyan" /> <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white" data-testid="contact-dubai-email">{CONTACT_EMAIL}</a></div>
              </div>
            </div>
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noreferrer"
              className="card-surface p-6 flex items-center gap-4 hover:border-cyan/30"
              data-testid="contact-whatsapp-card"
            >
              <div className="h-11 w-11 rounded-lg grid place-items-center bg-emerald-500/15 border border-emerald-400/30 text-emerald-300">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-white font-semibold">Chat on WhatsApp</div>
                <div className="text-xs text-ink-400">Replies within minutes during business hours</div>
              </div>
            </a>
            <div className="card-surface p-6">
              <div className="overline mb-2">Response time</div>
              <div className="text-sm text-ink-200">We respond to every inbound enquiry within one business day, often sooner.</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <SuccessPanel lead={success} onReset={() => setSuccess(null)} />
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                onSubmit={handleSubmit(onSubmit)}
                className="card-surface p-7 md:p-8"
                data-testid="contact-form"
                noValidate
              >
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-9 w-9 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/30 text-cyan">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <span className="overline">Schedule a Consultation</span>
                </div>

                <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} className="hidden" aria-hidden="true" />

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" error={errors.name?.message} testid="contact-name" required>
                    <input {...register("name", { required: "Required" })} className={inputCls} placeholder="Your name" />
                  </Field>
                  <Field label="Company name" error={errors.company?.message} testid="contact-company" required>
                    <input {...register("company", { required: "Required" })} className={inputCls} placeholder="Company" />
                  </Field>
                  <Field label="Work email" error={errors.email?.message} testid="contact-email" required>
                    <input
                      type="email"
                      {...register("email", { required: "Required", pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email" } })}
                      className={inputCls} placeholder="you@company.com"
                    />
                  </Field>
                  <Field label="Phone number" error={errors.phone?.message} testid="contact-phone" required>
                    <input
                      type="tel"
                      {...register("phone", { required: "Required", minLength: { value: 6, message: "Enter a valid phone" } })}
                      className={inputCls} placeholder="+91 / +971 / +1 …"
                    />
                  </Field>
                  <Field label="Country" error={errors.country?.message} testid="contact-country" required>
                    <select {...register("country", { required: "Required" })} className={inputCls + " appearance-none"} defaultValue="">
                      <option value="" disabled>Select your country</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Project type" error={errors.project_type?.message} testid="contact-project-type" required>
                    <select {...register("project_type", { required: "Required" })} className={inputCls + " appearance-none"} defaultValue="">
                      <option value="" disabled>Select a project type</option>
                      {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Estimated budget (optional)" testid="contact-budget" className="sm:col-span-2">
                    <select {...register("budget")} className={inputCls + " appearance-none"} defaultValue="">
                      <option value="">Prefer not to say</option>
                      {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="Project requirements" error={errors.project_details?.message} testid="contact-details" required>
                    <textarea
                      rows={6}
                      {...register("project_details", {
                        required: "Required",
                        minLength: { value: 10, message: "Please share a few more details (10+ characters)" },
                      })}
                      className={inputCls}
                      placeholder="What are you building, the outcome you're after, timelines and any constraints we should know about."
                    />
                  </Field>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button type="submit" disabled={submitting} className="btn-primary flex-1" data-testid="contact-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send enquiry <ArrowRight className="h-4 w-4" /></>}
                  </button>
                  <a href={whatsappUrl()} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="contact-whatsapp-cta">
                    <MessageCircle className="h-4 w-4" /> WhatsApp us
                  </a>
                </div>
                <p className="mt-3 text-[11px] text-ink-400">By submitting this form you agree we may contact you about your enquiry. We don&apos;t share your details with third parties.</p>
              </motion.form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function SuccessPanel({ lead, onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="card-surface p-7 md:p-8 space-y-5"
      data-testid="contact-success"
    >
      <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-5">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-300" />
          <div className="font-display text-lg font-semibold text-white">Thank you for contacting Zolvix Technologies</div>
        </div>
        <p className="mt-2 text-sm text-ink-200 leading-relaxed">
          Our team will review your requirements and get back to you shortly — typically within one business day. A confirmation has been sent to <span className="text-cyan">{lead.email}</span>.
        </p>
      </div>

      <div className="rounded-xl border border-cyan/25 bg-cyan/[0.05] p-5">
        <div className="font-display text-base font-semibold text-white">Need an immediate response? Chat with us on WhatsApp.</div>
        <a
          href={whatsappUrl(`Hi Zolvix Team, I just submitted an enquiry on your website (${lead.project_type || "a project"}). Looking forward to discussing.`)}
          target="_blank"
          rel="noreferrer"
          className="btn-primary mt-4"
          data-testid="contact-success-whatsapp"
        >
          <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
        </a>
      </div>

      <button onClick={onReset} className="btn-secondary w-full" data-testid="contact-success-reset">Submit another enquiry</button>
    </motion.div>
  );
}

const inputCls =
  "w-full rounded-lg bg-[#0F2B48] border border-white/[0.08] px-3.5 py-2.5 text-sm text-white placeholder:text-ink-400 focus:outline-none focus:border-cyan/50 focus:ring-2 focus:ring-cyan/20 transition";

function Field({ label, error, children, testid, className = "", required }) {
  return (
    <label className={"block " + className} data-testid={testid}>
      <span className="text-xs text-ink-200">
        {label}{required && <span className="text-cyan ml-1">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-[11px] text-red-300">{error}</span>}
    </label>
  );
}
